import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import 'react-block-ui/style.css';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';

import './App.css';
import './lib/FormValidator/Form/form-validator-styles.css';
import 'devextreme/dist/css/dx.light.css'; 

import TimeTaxSupportSystemClient from './system/TimeTaxSupportSystemClient';
import Environment from './lib/Environment';
import MessageCourier from './lib/MessageCourier';
import AppInRouterContext from './AppInRouterContext';
import UpdateNoticeDialog from './Components/Common/UpdateNoticeDialog';
import OfflineManagement from './Components/OfflineManagement';
import i18n, { LANGUAGE_LS_KEY, loadTranslationsFromS3 } from './i18n';

initializeIcons();
initializeFileTypeIcons();
interface IApp {
    newVersionAvailable: boolean;
    onUpdateVersion: () => void;
}

const environment = new Environment();
const system = TimeTaxSupportSystemClient.start(environment);
const appMessageCourier = new MessageCourier();

const App = (props: IApp) => {
    useEffect(() => {
      const syncTranslations = async () => {
        // Obtener el sistema de idiomas
        const languageSystem = system.getLanguageSystem();
        // Obtener los idiomas disponibles
        const { languages } = await languageSystem.findLanguages();
        // Guardar los idiomas en el sistema
        languageSystem.setLanguages(languages);

        // Obtener los idiomas disponibles a partir de los languages
        const { translations, translationsKey } = await loadTranslationsFromS3(languages);

        // Si no hay traducciones en S3, se ignora el proceso
        if (Object.keys(translations).length === 0) return;

        let isDifferent = false;

        // Comparar las traducciones de S3 con las que hay en localStorage
        for (const key of translationsKey) {
          const oldValue = localStorage.getItem(key);
          const newValue = translations[key];

          // Si no hay traducciones en localStorage, se marcan como diferentes
          if (oldValue !== newValue) {
            isDifferent = true;
            break;
          }
        }

        // Si las traducciones son iguales, no se hace nada
        if (!isDifferent) {
          console.info('Las traducciones están actualizadas.');
          return;
        }

        // Si las traducciones son diferentes, se actualizan las traducciones en localStorage
        console.warn('Traducciones desactualizadas. Actualizando...');
        for (const key of translationsKey) {
          localStorage.setItem(key, translations[key]);

          const lang = key.replace(LANGUAGE_LS_KEY, '');
          const parsed = JSON.parse(translations[key]);
          const bundle = parsed.translation ? parsed.translation : parsed;

          // Eliminar bundle antiguo si existe para evitar merge conflict
          if (i18n.hasResourceBundle(lang, 'translation')) {
            i18n.removeResourceBundle(lang, 'translation');
          }
          i18n.addResourceBundle(lang, 'translation', bundle, true, true);
        }

        console.info('Traducciones actualizadas en localStorage.');
      };

      syncTranslations();
    }, []);


    return (
        <React.Fragment>
            <OfflineManagement environment={environment}/>
            <ThemeProvider style={{ height: '100%' }} >
                <BrowserRouter >
                    {props.newVersionAvailable && (
                        <UpdateNoticeDialog
                            showDialog={props.newVersionAvailable}
                            toggleShowDialog={() => props.onUpdateVersion()}
                            onAccept={() => props.onUpdateVersion()}
                            onCancel={() => props.onUpdateVersion()}
                            hideCancelButton={true}
                        ></UpdateNoticeDialog>
                    )}
                    <AppInRouterContext system={system} environment={environment} courier={appMessageCourier} />
                </BrowserRouter>
            </ThemeProvider>
        </React.Fragment>
    );
};

export default App;
